/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Prisma Base Zod Schemas — Wire-Shape Mirrors for Contract Derivation
 *
 * =========================================================================
 * WHY THIS FILE EXISTS
 * =========================================================================
 *
 * `prisma-zod-generator` emits 2,704 schema files under
 * `server/src/generated/zod/` including a "pure" variant per model
 * (e.g. `Event.pure.ts`) that mirrors the Prisma column types. We cannot
 * directly import those from the shared-types package because:
 *
 *   1. They live under `server/src/`, and `shared-types` has `rootDir: "./src"`.
 *      Cross-package relative imports would break the tsgo build boundary.
 *   2. The pure variants use Prisma-domain types that do NOT match the
 *      JSON API wire shape:
 *        - `id: z.bigint()`        → wire uses `z.string()` (bigint serialized)
 *        - `eventDateTime: z.date()` → wire uses `z.string().datetime()`
 *        - relation fields as `z.unknown()` / `z.array(z.unknown())`
 *      A literal `.pick()` on the pure variant would yield fields that
 *      fail at runtime against real handler responses. We'd be forced to
 *      `.extend({...})` every scalar — at which point there is no "base".
 *
 * =========================================================================
 * OPTION CHOSEN: B (thin hand-curated base in shared-types)
 * =========================================================================
 *
 * The base schemas below match the **wire shape** (what handlers actually
 * emit in JSON responses) for each Prisma model. Contracts can now derive
 * via `.pick(...).extend(...)` without rewriting scalar types.
 *
 * Field names here mirror the generated Prisma model *exactly*. This is
 * load-bearing: the drift guard at `server/scripts/validate-zod-drift.ts`
 * compares the field names against the generated pure variants and fails
 * CI if they diverge. When a Prisma model gains a column, one of these
 * happens, both enforced by CI:
 *
 *   A) The column is safe-to-expose → added here → contracts can `.pick()` it.
 *   B) The column is intentionally kept off the wire → drift guard's
 *      allowlist entry says so. Silent drift is impossible.
 *
 * =========================================================================
 * RULES FOR EDITING THIS FILE
 * =========================================================================
 *
 * - Keep schemas in sync with `server/prisma/schema.prisma` column names.
 * - Prefer `z.string()` for ids that are bigint/cuid at the DB level.
 * - Use `z.string().datetime()` for Date columns.
 * - Nullable DB columns → `.nullable()` here (NOT `.optional()` — wire
 *   emits JSON `null`, not undefined).
 * - JSON columns → `z.record(z.string(), z.unknown()).nullable()` or a
 *   more specific shape if one exists.
 * - Relation fields (User, Organization, etc.) are NOT included here —
 *   contracts should model relations explicitly per use case.
 */

import * as z from "zod";

// Enum imports — we reuse `UserRoleSchema` from ../common/enums (no
// cycle). `EventStatusSchema` is inlined below to avoid an import cycle
// between `events.contract.ts` (which imports `EventBase` from here) and
// this file. If you need to change the event status enum, change both
// copies OR promote a single source to `../common/enums`.
import { UserRoleSchema } from "../common/enums";

/**
 * Inlined copy of the event status enum. Kept in lock-step with
 * `events.contract.ts#EventStatusSchema` (same member list).
 */
const EventStatusSchemaInline = z.enum([
	"PENDING",
	"CONFIRMED",
	"CANCELLED",
	"COMPLETED",
]);

// =============================================================================
// Event — mirrors Prisma model `Event` (table: core.booking)
// =============================================================================

/**
 * Base schema for Prisma `Event` model, wire-shape (JSON) compatible.
 *
 * Column-for-column mirror of the Prisma model. Contracts derive via
 * `.pick(...)` and optionally `.extend(...)` for additional API-shape
 * fields (e.g. `user_name`, `phone_e164`, `has_document`).
 */
export const EventBase = z.object({
	id: z.string(),
	eventDateTime: z.string().datetime(),
	type: z.string(),
	status: EventStatusSchemaInline,
	cancelledAt: z.string().datetime().nullable(),
	createdAt: z.string().datetime().nullable(),
	updatedAt: z.string().datetime().nullable(),
	version: z.number().int(),
	userId: z.string().nullable(),
	contactId: z.string(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	orgId: z.string(),
	uid: z.string().nullable(),
	eventTypeId: z.string().nullable(),
	// NOT NULL in `schema.prisma` — every booking is the interval
	// `[eventDateTime, endsAt)`. Modelled nullable here only while nothing read it.
	endsAt: z.string().datetime(),
	customFieldValues: z.record(z.string(), z.unknown()).nullable(),
});

// =============================================================================
// User — mirrors Prisma model `User` (table: core.user)
// =============================================================================

/**
 * Base schema for Prisma `User` model, wire-shape (JSON) compatible.
 */
export const UserBase = z.object({
	id: z.string(),
	name: z.string().nullable(),
	phoneE164: z.string().nullable(),
	email: z.string().nullable(),
	emailVerified: z.boolean(),
	passwordHash: z.string().nullable(),
	role: UserRoleSchema,
	isActive: z.boolean(),
	isVerified: z.boolean(),
	lastSignInAt: z.string().datetime().nullable(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	deletedAt: z.string().datetime().nullable(),
	version: z.number().int(),
	image: z.string().nullable(),
	banned: z.boolean().nullable(),
	banReason: z.string().nullable(),
	banExpires: z.string().datetime().nullable(),
	isAnonymous: z.boolean().nullable(),
	orgId: z.string().nullable(),
});

// =============================================================================
// UserProfile — mirrors Prisma model `UserProfile` (table: core.user_profile)
// =============================================================================

/**
 * Base schema for Prisma `UserProfile` model, wire-shape compatible.
 */
export const UserProfileBase = z.object({
	userId: z.string(),
	image: z.string().nullable(),
	age: z.number().int().nullable(),
	ageRecordedAt: z.string().datetime().nullable(),
	preferredLang: z.string().nullable(),
	document: z.record(z.string(), z.unknown()).nullable(),
	version: z.number().int(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	isFavorite: z.boolean(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});

// =============================================================================
// Type Exports
// =============================================================================

/** Wire-shape Event (Prisma `Event` model; JSON-serialized). */
export type EventBaseType = z.infer<typeof EventBase>;

/** Wire-shape User (Prisma `User` model; JSON-serialized). */
export type UserBaseType = z.infer<typeof UserBase>;

/** Wire-shape UserProfile (Prisma `UserProfile` model; JSON-serialized). */
export type UserProfileBaseType = z.infer<typeof UserProfileBase>;
